import type { Levels } from '@runkr/config/economy'
import type { RewardMsg } from '@runkr/schema'
import { NAME_KEY, writeLocal } from '../auth/storage'
import { Store } from '../net/store'
import type { ProfileApi, ProfileRow } from './profileApi'
import { economyLevels, toProfileView } from './profileView'
import type { ProfileView, WalletView } from './profileView'

/** `pending` until sign-in settles; `offline` = no Supabase (progress is not saved). */
export type ProfileStatus = 'pending' | 'offline' | 'loading' | 'ready' | 'error'

/**
 * The signed-in player's own `profiles` row (T2.4, D-053 slots `profile` / `wallet`). Loaded
 * after sign-in, refreshed after every `"reward"` (whose totals show at once) and when asked;
 * `null` offline or until the first read lands. A stale read never overwrites a newer one.
 */
export class ProfileStore {
  readonly view = new Store<ProfileView | null>(null)
  readonly wallet = new Store<WalletView | null>(null)
  readonly status = new Store<ProfileStatus>('pending')
  private api: ProfileApi | null = null
  private row: ProfileRow | null = null
  private seq = 0

  constructor(
    api: Promise<ProfileApi | null>,
    private readonly levels: Levels = economyLevels(),
  ) {
    this.view.subscribe((v) => {
      const w = this.wallet.get()
      if (!v) this.wallet.set(null)
      else if (!w || w.coins !== v.coins || w.gems !== v.gems) {
        this.wallet.set({ coins: v.coins, gems: v.gems })
      }
    })
    void api.then((a) => {
      this.api = a
      if (a) void this.refresh()
      else this.status.set('offline')
    })
  }

  /** The Supabase calls, null offline or before sign-in settles. */
  get source(): ProfileApi | null {
    return this.api
  }

  /** Read the row again. Resolves when done; failures keep the last good view. */
  async refresh(): Promise<void> {
    const api = this.api
    if (!api) return
    const seq = ++this.seq
    if (!this.row) this.status.set('loading')
    try {
      const row = await api.fetchProfile()
      if (seq !== this.seq) return
      this.setRow(row)
      this.status.set(row ? 'ready' : 'error')
    } catch (err) {
      if (seq !== this.seq) return
      console.warn('[profile] read failed', err instanceof Error ? err.message : err)
      if (!this.row) this.status.set('error')
    }
  }

  /** A saved race: show its totals now, then re-read the row for the counters. */
  applyReward(msg: RewardMsg): void {
    if (this.row) this.setRow({ ...this.row, xp: msg.totalXp, coins: msg.totalCoins })
    void this.refresh()
  }

  /**
   * Profile → Change name (T2.6, D-039): `set_display_name`, then the local name follows so
   * other tabs and the next join agree. Throws what the API throws (`NameRefused`, Error).
   */
  async rename(name: string): Promise<void> {
    const api = this.api
    if (!api) throw new Error('Progress is not saved offline')
    await api.setDisplayName(name)
    writeLocal(NAME_KEY, name)
    this.seq++
    if (this.row) this.setRow({ ...this.row, displayName: name })
    void this.refresh()
  }

  private setRow(row: ProfileRow | null): void {
    this.row = row
    this.view.set(row ? toProfileView(row, this.levels) : null)
  }
}
